// retrowser.json v1: types, schema + semantic validation, and the module interface (spec/retrowser-v1.md).
import { validate as schemaValidate } from './generated/validate-schema.js';

export type Platform = 'n64' | 'gba';
export interface FileRef { path: string; sha256: string; size: number }
export interface Manifest {
  retrowser: 1;
  id: string;
  title: string;
  platform: Platform;
  rom: { accept: string[]; sha1?: string[]; byteswap?: 'auto' | 'none'; maxSize?: number };
  files: { wasm: FileRef; js?: FileRef; data?: FileRef[] };
  module: { factory?: string; loop: 'frame' | 'asyncify' | 'mainloop' };
  video: 'framebuffer' | 'webgl';
  hz?: number;
  memory?: { initialMB?: number; maximumMB?: number; memory64?: boolean };
  save?: { kind: 'none' | 'eeprom' | 'sram' | 'flash' | 'controllerpak'; size?: number };
  controls?: { extra?: { bit: number; label: string }[] };
  options?: { id: number; label: string; type: 'bool' | 'int' | 'enum'; default?: number; min?: number; max?: number; choices?: { value: number; label: string }[] }[];
  /** v1.2: online play. players > 1 opts in; model: lockstep (host-dictated boot, default) or link (live session). */
  net?: { players?: number; model?: 'lockstep' | 'link' };
  shares?: { provides?: string[]; consumes?: string[] };
  /** v1.1: files the player can hand to the game. */
  fileInputs?: { id: number; label: string; accept: string[]; maxSize?: number }[];
  license?: string;
  source?: string;
}

export const LIMITS = {
  fileBytes: 64 * 1024 * 1024,
  gameBytes: 256 * 1024 * 1024,
  manifestBytes: 256 * 1024,
  romBytes: 256 * 1024 * 1024,
} as const;

export type ValidationResult = { ok: true; manifest: Manifest; warnings: string[] } | { ok: false; errors: string[] };

export function allFiles(m: Manifest): FileRef[] {
  return [m.files.wasm, ...(m.files.js ? [m.files.js] : []), ...(m.files.data ?? [])];
}

export function validateManifest(json: unknown): ValidationResult {
  if (!schemaValidate(json)) {
    const errors = (schemaValidate.errors ?? []).map((e) => `${e.instancePath || '/'} ${e.message ?? e.keyword}${
      e.keyword === 'additionalProperties' ? ` (${String(e.params.additionalProperty)})` : ''}`);
    if (errors.some((e) => /required property 'size'/.test(e))) errors.push('every file needs "size" (run rtw-hash to add it)');
    return { ok: false, errors };
  }
  const m = json as Manifest;
  const errors: string[] = [];
  const warnings: string[] = [];
  if (m.files.js && !m.module.factory) errors.push('/module/factory is required when files.js is present');
  if (!m.files.js && m.module.factory) errors.push('/module/factory needs files.js');
  if (!m.files.js && m.module.loop !== 'frame') errors.push('/module/loop must be "frame" when there is no files.js');
  if (m.memory?.initialMB && m.memory?.maximumMB && m.memory.maximumMB < m.memory.initialMB) {
    errors.push('/memory/maximumMB must be >= initialMB');
  }
  // Text shown to the player: no control characters and no bidirectional overrides (spoofed titles/labels).
  // eslint-disable-next-line no-control-regex
  const BAD_TEXT = /[\u0000-\u001f\u007f-\u009f\u061c\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/;
  const texts: [string, string][] = [['/title', m.title], ['/license', m.license ?? '']];
  (m.controls?.extra ?? []).forEach((x, i) => texts.push([`/controls/extra/${i}/label`, x.label]));
  (m.fileInputs ?? []).forEach((f, i) => texts.push([`/fileInputs/${i}/label`, f.label]));
  (m.options ?? []).forEach((o, i) => {
    texts.push([`/options/${i}/label`, o.label]);
    (o.choices ?? []).forEach((c, j) => texts.push([`/options/${i}/choices/${j}/label`, c.label]));
  });
  for (const [where, t] of texts) if (BAD_TEXT.test(t)) errors.push(`${where} contains control or text-direction characters`);
  if (m.source && !/^https:\/\/github\.com\/[A-Za-z0-9-]+\/[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._\-/]*)?$/.test(m.source)) {
    warnings.push('/source is not a https://github.com/<owner>/<repo> URL; the host links the repository instead');
  }
  const paths = new Set<string>();
  let total = 0;
  for (const f of allFiles(m)) {
    if (paths.has(f.path)) errors.push(`duplicate file path ${f.path}`);
    paths.add(f.path);
    total += f.size;
  }
  if (total > LIMITS.gameBytes) errors.push(`files total ${total} bytes exceeds ${LIMITS.gameBytes}`);
  const ids = new Set<number>();
  for (const o of m.options ?? []) {
    if (ids.has(o.id)) errors.push(`duplicate option id ${o.id}`);
    ids.add(o.id);
    if (o.type === 'enum' && !o.choices?.length) errors.push(`option ${o.id}: enum needs choices`);
    if (o.min !== undefined && o.max !== undefined && o.min > o.max) errors.push(`option ${o.id}: min > max`);
  }
  const fids = new Set<number>();
  for (const f of m.fileInputs ?? []) {
    if (fids.has(f.id)) errors.push(`duplicate fileInputs id ${f.id}`);
    fids.add(f.id);
  }
  if (m.net?.model && (m.net.players ?? 1) < 2) warnings.push('/net/model has no effect unless net.players > 1');
  const bits = new Set<number>();
  for (const x of m.controls?.extra ?? []) {
    if (bits.has(x.bit)) errors.push(`duplicate controls.extra bit ${x.bit}`);
    bits.add(x.bit);
  }
  if (m.platform === 'n64' && m.rom.accept.some((e) => !['.z64', '.n64', '.v64'].includes(e))) {
    warnings.push('n64 rom.accept has extensions other than .z64/.n64/.v64');
  }
  if (m.platform === 'gba' && m.rom.accept.some((e) => e !== '.gba' && e !== '.bin')) {
    warnings.push('gba rom.accept has extensions other than .gba/.bin');
  }
  return errors.length ? { ok: false, errors } : { ok: true, manifest: m, warnings };
}

// ---- Module interface -------------------------------------------------------------------------------------

/** Exports every module must provide. */
export const EXPORTS_ALWAYS = [
  'rtw_rom_buffer', 'rtw_save_load', 'rtw_start', 'rtw_pause',
  'rtw_input', 'rtw_mouse', 'rtw_resize',
  'rtw_audio_rate', 'rtw_audio_drain', 'rtw_audio_buffer',
  'rtw_save_ptr', 'rtw_save_size', 'rtw_save_generation',
  'rtw_option',
] as const;
export const EXPORTS_FRAME = ['rtw_frame'] as const;
export const EXPORTS_FRAMEBUFFER = ['rtw_fb', 'rtw_fb_width', 'rtw_fb_height'] as const;
/** v1.2, required when net.players > 1. */
export const EXPORTS_NET = ['rtw_net_session', 'rtw_net_peer', 'rtw_net_buffer', 'rtw_net_recv'] as const;
/** v1.2 host imports that only a net build (net.players > 1) may use. */
export const IMPORTS_NET = ['rtw_net_send', 'rtw_net_stat'] as const;
/** v1.1, required when the manifest has fileInputs. */
export const EXPORTS_FILE = ['rtw_file_buffer', 'rtw_file_ready'] as const;
/** v1.1, optional: called only when the module exports them. */
export const EXPORTS_OPTIONAL = ['rtw_rstick', 'rtw_wheel'] as const;

/** Imports the host provides (module "env"). */
export const HOST_IMPORTS = ['rtw_rumble', 'rtw_export_file', 'rtw_build_id', 'rtw_log', 'rtw_pointer_lock', 'rtw_request_file', 'rtw_option_changed',
  ...IMPORTS_NET] as const;

/** v1.2: the net model of a build that plays online, or null. */
export function netModel(m: Pick<Manifest, 'net'>): 'lockstep' | 'link' | null {
  return (m.net?.players ?? 1) > 1 ? m.net?.model ?? 'lockstep' : null;
}

/** Number of i32 parameters per export (results are always one i32). */
export const EXPORT_ARITY: Record<string, number> = {
  rtw_rom_buffer: 1, rtw_save_load: 2, rtw_start: 0, rtw_frame: 0, rtw_pause: 1,
  rtw_input: 4, rtw_mouse: 3, rtw_fb: 0, rtw_fb_width: 0, rtw_fb_height: 0, rtw_resize: 3,
  rtw_audio_rate: 0, rtw_audio_drain: 0, rtw_audio_buffer: 0,
  rtw_save_ptr: 0, rtw_save_size: 0, rtw_save_generation: 0, rtw_option: 2,
  rtw_net_session: 2, rtw_net_peer: 2, rtw_net_buffer: 1, rtw_net_recv: 3,
  rtw_rstick: 3, rtw_wheel: 1, rtw_file_buffer: 1, rtw_file_ready: 2,
};
export const IMPORT_ARITY: Record<string, number> = {
  rtw_rumble: 2, rtw_export_file: 4, rtw_build_id: 1, rtw_log: 2, rtw_pointer_lock: 1, rtw_request_file: 1, rtw_option_changed: 2,
  rtw_net_send: 4, rtw_net_stat: 2,
};

export function requiredExports(m: Pick<Manifest, 'module' | 'video' | 'net' | 'fileInputs'>): string[] {
  const out: string[] = [...EXPORTS_ALWAYS];
  if (m.module.loop === 'frame') out.push(...EXPORTS_FRAME);
  if (m.video === 'framebuffer') out.push(...EXPORTS_FRAMEBUFFER);
  if ((m.net?.players ?? 1) > 1) out.push(...EXPORTS_NET);
  if (m.fileInputs?.length) out.push(...EXPORTS_FILE);
  return out;
}

/** Input bit layouts (active-high: 1 = pressed). Extra named buttons use bits 16..31. */
export const GBA_KEYS = { A: 0, B: 1, Select: 2, Start: 3, Right: 4, Left: 5, Up: 6, Down: 7, R: 8, L: 9 } as const;
export const N64_BUTTONS = {
  CRight: 0x0001, CLeft: 0x0002, CDown: 0x0004, CUp: 0x0008, R: 0x0010, L: 0x0020,
  DRight: 0x0100, DLeft: 0x0200, DDown: 0x0400, DUp: 0x0800, Start: 0x1000, Z: 0x2000, B: 0x4000, A: 0x8000,
} as const;
